import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatGNF, statusLabel, statusColor } from "../constants/mcevents";
import { Inbox, Receipt, FileSignature, Loader2, X, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Dashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Validate dialog
  const [validating, setValidating] = useState(null); // contract object
  const [agreedAmount, setAgreedAmount] = useState(0);
  const [busy, setBusy] = useState(false);

  // Commission pay
  const [payingCommission, setPayingCommission] = useState(null);
  const [provider, setProvider] = useState("orange_money");
  const [phone, setPhone] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [b, c, p] = await Promise.all([
        api.get("/bookings/mine"),
        api.get("/contracts/mine"),
        api.get("/payments/mine"),
      ]);
      setBookings(b.data.bookings || []);
      setContracts(c.data.contracts || []);
      setPayments(p.data.payments || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const respond = async (bookingId, action) => {
    try {
      await api.patch(`/bookings/${bookingId}/respond?action=${action}`);
      toast.success(action === "accept" ? "Demande acceptée" : "Demande refusée");
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitValidate = async () => {
    setBusy(true);
    try {
      await api.patch(`/contracts/${validating.id}/validate`, { agreed_amount_gnf: Number(agreedAmount) });
      toast.success("Validation enregistrée");
      setValidating(null);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitCommission = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/contracts/${payingCommission.id}/pay-commission`, { payment_provider: provider, payment_phone: phone });
      if (data.payment_url) {
        toast.info("Redirection vers le paiement sécurisé FedaPay…");
        window.location.href = data.payment_url;
        return;
      }
      toast.success(`Commission payée · Réf ${data.payment.reference}`);
      setPayingCommission(null);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const stats = {
    bookings: bookings.length,
    contracts: contracts.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    revenue: payments.filter((p) => p.status === "succeeded").reduce((s, p) => s + (p.amount_gnf || 0), 0),
  };

  return (
    <div className="container-mc py-10" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-8">
        <div>
          <p className="overline mb-2">{user?.role === "artist" ? "Espace artiste" : "Espace client"}</p>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Tableau de bord</h1>
        </div>
        {user?.role === "artist" && (
          <Link to="/artist/onboarding" className="btn-outline" data-testid="edit-profile-link">Éditer mon profil</Link>
        )}
      </div>

      {/* STATS GRID */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-0 card-mc mb-10">
        {[
          { label: "Sollicitations", value: stats.bookings, color: "text-foreground" },
          { label: user?.role === "artist" ? "À répondre" : "En attente", value: stats.pending, color: "text-[hsl(var(--primary))]" },
          { label: "Contrats", value: stats.contracts, color: "text-[hsl(var(--accent))]" },
          { label: user?.role === "artist" ? "Versé" : "Dépensé", value: formatGNF(stats.revenue), color: "text-foreground", small: true },
        ].map((s, i) => (
          <div key={i} className={`p-6 ${i < 3 ? "md:border-r border-border" : ""} ${i < 2 ? "border-b md:border-b-0" : ""}`}>
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{s.label}</p>
            <p className={`mt-2 font-display font-black tracking-tighter ${s.small ? "text-2xl" : "text-4xl"} ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* TABS */}
      <div className="flex gap-2 border-b border-border mb-6">
        {[
          { id: "bookings", label: "Sollicitations", Icon: Inbox },
          { id: "contracts", label: "Contrats", Icon: FileSignature },
          { id: "payments", label: "Paiements", Icon: Receipt },
        ].map(({ id, label, Icon }) => (
          <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)} className={`px-4 py-3 flex items-center gap-2 text-sm font-bold border-b-2 transition-colors ${tab === id ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center font-mono text-sm uppercase tracking-wider text-muted-foreground py-12">Chargement…</div>
      ) : (
        <>
          {tab === "bookings" && (
            <BookingsList bookings={bookings} role={user?.role} onRespond={respond} />
          )}
          {tab === "contracts" && (
            <ContractsList
              contracts={contracts}
              role={user?.role}
              onValidate={(c) => { setValidating(c); setAgreedAmount(c.agreed_amount_gnf || c.booking?.budget_gnf || 0); }}
              onPayCommission={(c) => setPayingCommission(c)}
            />
          )}
          {tab === "payments" && <PaymentsList payments={payments} />}
        </>
      )}

      {/* VALIDATE MODAL */}
      {validating && (
        <Modal title="Valider le contrat" onClose={() => setValidating(null)}>
          <p className="text-sm text-muted-foreground mb-4">Confirmez le montant final convenu entre les deux parties. Une fois les deux côtés validés, le contrat passera au statut <strong>Validé</strong>.</p>
          <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Montant convenu (GNF)</label>
          <input data-testid="validate-amount" type="number" min="0" value={agreedAmount} onChange={(e) => setAgreedAmount(e.target.value)} className="mt-1 w-full border border-border px-3 py-2.5 rounded-sm bg-card font-mono mb-3" />
          <p className="text-xs text-muted-foreground mb-4">Commission artiste estimée : <strong>{formatGNF(Math.round(Number(agreedAmount) * 0.1))}</strong> (10%)</p>
          <button onClick={submitValidate} disabled={busy} className="btn-primary w-full" data-testid="confirm-validate">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Confirmer la validation
          </button>
        </Modal>
      )}

      {/* COMMISSION MODAL */}
      {payingCommission && (
        <Modal title="Payer la commission" onClose={() => setPayingCommission(null)}>
          <div className="card-mc bg-foreground text-background p-5 mb-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/60">Commission due (10%)</p>
            <p className="font-display font-black text-3xl mt-1">{formatGNF(payingCommission.commission_gnf)}</p>
          </div>
          <p className="overline mb-2">Méthode de paiement</p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button type="button" data-testid="com-provider-orange" onClick={() => setProvider("orange_money")} className={`p-3 border rounded-sm flex items-center gap-2 ${provider === "orange_money" ? "border-foreground bg-foreground text-background" : "border-border"}`}>
              <span className="pill-orange">OM</span><span className="text-sm font-bold">Orange Money</span>
            </button>
            <button type="button" data-testid="com-provider-mtn" onClick={() => setProvider("mtn_momo")} className={`p-3 border rounded-sm flex items-center gap-2 ${provider === "mtn_momo" ? "border-foreground bg-foreground text-background" : "border-border"}`}>
              <span className="pill-mtn">MoMo</span><span className="text-sm font-bold">MTN MoMo</span>
            </button>
          </div>
          <input data-testid="com-phone" required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Numéro de téléphone" className="w-full border border-border px-3 py-2.5 rounded-sm bg-card font-mono mb-3" />
          <button onClick={submitCommission} disabled={busy || !phone} className="btn-primary w-full" data-testid="confirm-commission">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Payer & Clôturer le contrat
          </button>
          <p className="text-[10px] text-muted-foreground mt-2 italic text-center">Paiement sécurisé via Mobile Money.</p>
        </Modal>
      )}
    </div>
  );
}

function BookingsList({ bookings, role, onRespond }) {
  if (bookings.length === 0) return <EmptyState text="Aucune sollicitation pour le moment." />;
  return (
    <div className="space-y-3" data-testid="bookings-list">
      {bookings.map((b) => (
        <div key={b.id} className="card-mc p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
          <div className="md:col-span-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{new Date(b.created_at).toLocaleDateString("fr-FR")}</p>
            <p className="font-display font-black text-lg">{role === "artist" ? b.client_name : b.artist_stage_name}</p>
            <p className="text-xs text-muted-foreground">{b.artist_category}</p>
          </div>
          <div className="md:col-span-3">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Événement</p>
            <p className="font-semibold text-sm">{b.event_type}</p>
            <p className="text-xs text-muted-foreground">{b.event_date} · {b.location}</p>
          </div>
          <div className="md:col-span-2">
            <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Budget</p>
            <p className="font-mono font-bold">{formatGNF(b.budget_gnf)}</p>
          </div>
          <div className="md:col-span-2">
            <span className={`status-badge ${statusColor(b.status)}`}>{statusLabel(b.status)}</span>
          </div>
          <div className="md:col-span-2 flex gap-2 justify-end">
            {role === "artist" && b.status === "pending" && (
              <>
                <button data-testid={`accept-${b.id}`} onClick={() => onRespond(b.id, "accept")} className="btn-secondary text-xs px-3 py-2">Accepter</button>
                <button data-testid={`decline-${b.id}`} onClick={() => onRespond(b.id, "decline")} className="btn-outline text-xs px-3 py-2">Refuser</button>
              </>
            )}
          </div>
          {b.message && (
            <div className="md:col-span-12 border-t border-border pt-3 text-sm text-muted-foreground italic">"{b.message}"</div>
          )}
        </div>
      ))}
    </div>
  );
}

function ContractsList({ contracts, role, onValidate, onPayCommission }) {
  if (contracts.length === 0) return <EmptyState text="Aucun contrat encore." />;
  return (
    <div className="space-y-3" data-testid="contracts-list">
      {contracts.map((c) => (
        <div key={c.id} className="card-mc p-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
            <div className="md:col-span-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Contrat</p>
              <p className="font-display font-black text-lg">{role === "artist" ? c.client_name : c.artist_stage_name}</p>
              <p className="text-xs text-muted-foreground font-mono">{c.id.slice(0, 8).toUpperCase()}</p>
            </div>
            <div className="md:col-span-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Événement</p>
              <p className="font-semibold text-sm">{c.booking?.event_type || "—"}</p>
              <p className="text-xs text-muted-foreground">{c.booking?.event_date} · {c.booking?.location}</p>
            </div>
            <div className="md:col-span-2">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Montant convenu</p>
              <p className="font-mono font-bold">{c.agreed_amount_gnf > 0 ? formatGNF(c.agreed_amount_gnf) : "—"}</p>
              {c.commission_gnf > 0 && <p className="text-xs text-[hsl(var(--accent))] font-mono">+{formatGNF(c.commission_gnf)} comm.</p>}
            </div>
            <div className="md:col-span-2">
              <span className={`status-badge ${statusColor(c.status)}`}>{statusLabel(c.status)}</span>
              <div className="flex gap-1 mt-2 text-[10px] font-mono">
                {c.client_validated && <span className="text-[hsl(var(--accent))]">✓ Client</span>}
                {c.artist_validated && <span className="text-[hsl(var(--accent))]">✓ Artiste</span>}
              </div>
            </div>
            <div className="md:col-span-2 flex flex-col gap-2 items-stretch md:items-end">
              {(c.status === "in_progress" || c.status === "open") && (
                <button onClick={() => onValidate(c)} data-testid={`validate-${c.id}`} className="btn-secondary text-xs px-3 py-2 inline-flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" /> Valider
                </button>
              )}
              {c.status === "validated" && role === "artist" && (
                <button onClick={() => onPayCommission(c)} data-testid={`pay-com-${c.id}`} className="btn-primary text-xs px-3 py-2">Payer commission</button>
              )}
              {c.status === "closed" && <span className="text-[11px] font-mono uppercase tracking-wider text-[hsl(var(--accent))]">✓ Clôturé</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PaymentsList({ payments }) {
  if (payments.length === 0) return <EmptyState text="Aucun paiement enregistré." />;
  return (
    <div className="card-mc overflow-x-auto" data-testid="payments-list">
      <table className="w-full text-sm">
        <thead className="bg-muted">
          <tr className="text-left">
            {["Date", "Type", "Montant", "Méthode", "Téléphone", "Référence", "Statut"].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.id} className="border-t border-border">
              <td className="px-4 py-3 font-mono text-xs">{new Date(p.created_at).toLocaleString("fr-FR")}</td>
              <td className="px-4 py-3"><span className="status-badge bg-muted">{p.type === "unlock_fee" ? "Mise en relation" : "Commission"}</span></td>
              <td className="px-4 py-3 font-mono font-bold">{formatGNF(p.amount_gnf)}</td>
              <td className="px-4 py-3">{p.provider === "orange_money" ? <span className="pill-orange">OM</span> : <span className="pill-mtn">MoMo</span>}</td>
              <td className="px-4 py-3 font-mono text-xs">{p.phone_masked}</td>
              <td className="px-4 py-3 font-mono text-xs">{p.reference}</td>
              <td className="px-4 py-3"><span className="status-badge bg-[hsl(var(--accent))] text-white">{p.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="card-mc p-12 text-center">
      <p className="text-muted-foreground">{text}</p>
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-card w-full max-w-md card-mc border-foreground">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <p className="font-display font-black text-xl tracking-tighter">{title}</p>
          <button onClick={onClose} data-testid="modal-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
